// src/routes/RouteConfig.tsx
import React, { Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import ErrorBoundary from '../components/ErrorBoundary'
import LandingPage from '../pages/LandingPage'
import LoginPage from '../pages/LoginPage'
import SignupPage from '../pages/SignupPage'
import ForgotPasswordPage from '../pages/ForgotPasswordPage'
import ResetPasswordPage from '../pages/ResetPasswordPage'
import ConfirmPage from '../pages/ConfirmPage'
import PricingPage from '../pages/PricingPage'
import PrivacyPolicyPage from '../pages/PrivacyPolicyPage'
import TermsPage from '../pages/TermsPage'
import TeamPage from '../pages/TeamPage'
import AppTeamPage from '../pages/AppTeamPage'
import UnsubscribePage from '../pages/UnsubscribePage'
import CalendarPage from '../pages/CalendarPage'
import SetPasswordPage from '../pages/SetPasswordPage'
import ChangePasswordPage from '../pages/ChangePasswordPage'
import { JournalLayout } from '../components/journal/Layout/JournalLayout'
import { ProtectedRoute } from './ProtectedRoute'
import { PremiumRoute } from './PremiumRoute'
import AffiliatePortalPage from '../pages/AffiliatePortalPage'
import { AffiliateRoute } from './AffiliateRoute'
import { lazyImport, lazyNamedImport } from '../utils/lazyImport'
import AIAnalysisPage from '../pages/AIAnalysisPage'
import TrialPage from '../pages/TrialPage'

const SettingsPage = lazyImport(
  () => import('../pages/SettingsPage'),
  "Settings"
)

const NotesPage = lazyImport(
  () => import('../pages/NotesPage'),
  "Notes"
)

const TradingModelsPage = lazyImport(
  () => import('../pages/TradingModelsPage'),
  "Trading Models"
)

const ResourcesPage = lazyImport(
  () => import('../pages/ResourcesPage'),
  "Resources"
)

const ComprehensiveComparisonPage = lazyImport(
  () => import('../pages/ComprehensiveComparisonPage'),
  "Comprehensive Comparison"
)

const BestTradingJournalPage = lazyImport(
  () => import('../pages/BestTradingJournalPage'),
  "Best Trading Journal"
)

const DashboardView = lazyNamedImport(
  () => import('../components/journal/Dashboard/DashboardView'),
  'DashboardView',
  "Dashboard"
)

const TradeLogView = lazyNamedImport(
  () => import('../components/journal/TradeLog/TradeLogView'),
  'TradeLogView',
  "Trade Log"
)

const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-full py-12">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
  </div>
)

const RouteConfig: React.FC = () => {
  console.log('RouteConfig: Rendering RouteConfig component')
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/confirm" element={<ConfirmPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/trial" element={<TrialPage />} />
      <Route path="/resources" element={<Suspense fallback={<LoadingSpinner />}><ResourcesPage /></Suspense>} />
      <Route path="/comprehensive-trading-journal-comparison" element={<Suspense fallback={<LoadingSpinner />}><ComprehensiveComparisonPage /></Suspense>} />
      <Route path="/best-trading-journal-software-2025" element={<Suspense fallback={<LoadingSpinner />}><BestTradingJournalPage /></Suspense>} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/team" element={<TeamPage />} />
      <Route
        path="/unsubscribe"
        element={<UnsubscribePage />}
      />

      {/* Set Password (protected) */}
      <Route
        path="/set-password"
        element={
          <ProtectedRoute>
            <SetPasswordPage />
          </ProtectedRoute>
        }
      />

      {/* Protected App */}
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <ErrorBoundary>
              <JournalLayout />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      >
        <Route
          path="calendar"
          element={
            <ErrorBoundary>
              <CalendarPage />
            </ErrorBoundary>
          }
        />
        <Route
          path="tradelog"
          element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <TradeLogView />
              </Suspense>
            </ErrorBoundary>
          }
        />
        <Route
          path="notes"
          element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <NotesPage />
              </Suspense>
            </ErrorBoundary>
          }
        />
        <Route
          path="tradingModels"
          element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <TradingModelsPage />
              </Suspense>
            </ErrorBoundary>
          }
        />
        <Route
          path="dashboard"
          element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <DashboardView />
              </Suspense>
            </ErrorBoundary>
          }
        />
        <Route
          path="settings"
          element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <SettingsPage />
              </Suspense>
            </ErrorBoundary>
          }
        />
        <Route
          path="ai-analysis"
          element={
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <PremiumRoute>
                  <AIAnalysisPage />
                </PremiumRoute>
              </Suspense>
            </ErrorBoundary>
          }
        />
        <Route
          path="change-password"
          element={
            <ErrorBoundary>
              <ChangePasswordPage />
            </ErrorBoundary>
          }
        />

        {/* In-app Team page for trial/subscribed users */}
        <Route
          path="team"
          element={
            <ErrorBoundary>
              <AppTeamPage />
            </ErrorBoundary>
          }
        />

        {/* Affiliate Portal - Now inside app routes with sidebar */}
        <Route
          path="affiliate-portal"
          element={
            <ErrorBoundary>
              <AffiliateRoute>
                <AffiliatePortalPage />
              </AffiliateRoute>
            </ErrorBoundary>
          }
        />

        <Route index element={<Navigate to="calendar" replace />} />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default RouteConfig